/**
 * Custom React hooks
 */

import { useIntersection } from "@mantine/hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStickToBottom } from "use-stick-to-bottom";

/**
 * Stick-to-bottom auto-scroll behavior customized for AIChatConversationView.
 *
 * @returns
 */
export function useAIChatStickToBottom(): [
	React.MutableRefObject<HTMLElement | null> & React.RefCallback<HTMLElement>,
	React.MutableRefObject<HTMLElement | null> & React.RefCallback<HTMLElement>,
	(element: any) => void,
	boolean,
	() => void,
] {
	// Stick to bottom as new content streams in
	const {
		scrollRef: viewportRef,
		contentRef,
		isAtBottom: atBottom,
		scrollToBottom,
		escapedFromLock,
		stopScroll,
	} = useStickToBottom({
		initial: "instant",
	});

	// Stop auto-scrolling when streaming message reaches the top
	const { ref: streamingMessageRef, entry: streamingMessageIntersection } = useIntersection({
		root: viewportRef.current,
		rootMargin: "0px 0px -100% 0px", // Triggers when the element touches the top of the viewport
		threshold: 0,
	});

	useEffect(() => {
		if (!escapedFromLock && streamingMessageIntersection?.isIntersecting) {
			stopScroll();
		}
	}, [streamingMessageIntersection?.isIntersecting]);

	return [viewportRef, contentRef, streamingMessageRef, atBottom, scrollToBottom];
}

export function useAIChatScrollToPrompt(
	messages: any,
	styles: CSSModuleClasses,
): [
	React.RefObject<HTMLDivElement | null>,
	React.RefObject<HTMLDivElement | null>,
	null,
	boolean,
	() => void,
] {
	// Feature: Scroll To Prompt

	const viewportRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	const scrollToPrompt = useCallback(() => {
		// Get data
		if (!viewportRef.current || !contentRef.current) {
			console.error("Scroll to prompt failed: Missing one or more refs.");
			return;
		}

		const lastPrompt = Array.from(
			contentRef.current.querySelectorAll(`.${styles.message_role_user}`),
		).pop() as HTMLElement;
		if (!lastPrompt) {
			console.error("Scroll to prompt failed: No prompt found.");
			return;
		}

		// Adjust content height
		const status = contentRef.current.querySelector(`.${styles.status}`) as HTMLElement;
		const offset = status ? lastPrompt.offsetHeight + status.offsetHeight : lastPrompt.offsetHeight;
		const adjustment = viewportRef.current.clientHeight - offset;
		const currentContentHeight = contentRef.current.offsetHeight;
		contentRef.current.style.minHeight = `${currentContentHeight + adjustment}px`;

		// Scroll to prompt
		lastPrompt.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [styles]);

	useEffect(() => {
		if (messages.length && messages.at(-1).role === "user") {
			scrollToPrompt();
		}
	}, [messages.length]);

	// Feature: Scroll To Bottom

	// const { ref: contentHeightRef, height: contentHeight } = useElementSize();
	const [scrollTop, setScrollTop] = useState<number>(0);
	const [atBottom, setAtBottom] = useState<boolean>(true);

	const scrolledUpDistance = (element: HTMLElement) => {
		return Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight);
	};

	const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
		viewportRef.current!.scrollTo({
			top: viewportRef.current!.scrollHeight,
			behavior: behavior,
		});
	};

	const handleScroll = () => {
		setScrollTop(viewportRef.current!.scrollTop);
	};

	useEffect(() => {
		viewportRef.current?.addEventListener("scroll", handleScroll);
	}, [viewportRef.current]);

	useEffect(() => {
		const distance = scrolledUpDistance(viewportRef.current!);
		setAtBottom(distance < 48);
	}, [scrollTop, viewportRef.current?.scrollHeight]);

	const streamingMessageRef = null; // This is a dummy ref to match stick-to-bottom

	return [viewportRef, contentRef, streamingMessageRef, atBottom, scrollToBottom];
}
